import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
  IconButton,
  Popover,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  Divider,
  useMediaQuery,
  useTheme,
  alpha,
} from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import {
  getUnits,
  createUnit,
  updateUnit,
  deleteUnit,
} from "../../../Services/Services";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const UnitManager = () => {
  const [units, setUnits] = useState([]);
  const [open, setOpen] = useState(false);

  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [expandedUnit, setExpandedUnit] = useState(null);

  // Add Mode → multiple rows
  const [unitForms, setUnitForms] = useState([{ name: "", symbol: "" }]);
  const [touched, setTouched] = useState([{ name: false, symbol: false }]);

  // Edit Mode → single row
  const [unitForm, setUnitForm] = useState({
    name: "",
    symbol: "",
    created_by: localStorage.getItem("user_id"),
  });

  const [editId, setEditId] = useState(null);

  // Popover delete
  const [anchorEl, setAnchorEl] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");

  const userId = localStorage.getItem("user_id");

  // ── Theme-aware helpers ──
  const tableHeadBg = isDark
    ? alpha(theme.palette.primary.main, 0.15)
    : "#f0f4ff";

  const rowBg = (index) =>
    index % 2 === 0
      ? isDark
        ? alpha(theme.palette.common.white, 0.035)
        : "#fafafa"
      : theme.palette.background.paper;

  // ── Fetch ──
  const fetchUnits = async () => {
    const data = await getUnits();
    setUnits(data);
  };

  useEffect(() => {
    fetchUnits();
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMobile ? "auto" : "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMobile]);

  // ── Add Row ──
  const handleAddRow = () => {
    if (unitForms.length >= 5) {
      toast.error("You can add maximum 5 units");
      return;
    }
    setUnitForms([...unitForms, { name: "", symbol: "" }]);
    setTouched([...touched, { name: false, symbol: false }]);
  };

  const handleRemoveField = (index) => {
    setUnitForms(unitForms.filter((_, i) => i !== index));
    setTouched(touched.filter((_, i) => i !== index));
  };

  const handleChange = (index, field, value) => {
    const updatedForms = [...unitForms];
    updatedForms[index][field] = value;
    setUnitForms(updatedForms);
  };

  const handleBlur = (index, field) => {
    const updatedTouched = [...touched];
    updatedTouched[index][field] = true;
    setTouched(updatedTouched);
  };

  // ── Save ──
  const handleSave = async () => {
    if (editId) {
      if (!unitForm.name.trim() || !unitForm.symbol.trim()) {
        toast.error("Both fields are required!");
        return;
      }
      try {
        await updateUnit(editId, unitForm);
        toast.success("Unit updated successfully!");
        fetchUnits();
        setOpen(false);
      } catch (error) {
        toast.error("Something went wrong!");
      }
    } else {
      const allTouched = unitForms.map(() => ({ name: true, symbol: true }));
      setTouched(allTouched);

      const isValid = unitForms.every(
        (form) => form.name.trim() !== "" && form.symbol.trim() !== ""
      );
      if (!isValid) {
        toast.error("Please fill all required fields!");
        return;
      }

      try {
        for (let form of unitForms) {
          await createUnit({ ...form, created_by: userId });
        }
        toast.success("Units added successfully!");
        fetchUnits();
        setUnitForms([{ name: "", symbol: "" }]);
        setTouched([{ name: false, symbol: false }]);
        setOpen(false);
      } catch (error) {
        toast.error("Something went wrong!");
      }
    }
  };

  // ── Edit ──
  const handleEdit = (unit) => {
    setUnitForm({
      name: unit.name || "",
      symbol: unit.symbol || "",
      created_by: userId,
    });
    setEditId(unit.id);
    setOpen(true);
  };

  // ── Delete ──
  const handleDeleteClick = (event, id) => {
    setAnchorEl(event.currentTarget);
    setDeleteId(id);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setDeleteId(null);
  };

  const confirmDelete = async () => {
    if (deleteId) {
      await deleteUnit(deleteId);
      setUnits((prev) => prev.filter((unit) => unit.id !== deleteId));
      handlePopoverClose();
      toast.success("Unit deleted successfully!");
    }
  };

  const openPopover = Boolean(anchorEl);

  // ── Filter ──
  const filteredUnits = units.filter(
    (unit) =>
      unit.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      unit.symbol.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box
      sx={{
        px: 0,
        pt: 0,
        mt: { xs: 3, md: 1 },
        maxWidth: 1200,
        mx: "auto",
        bgcolor: "background.default",
        minHeight: "100%",
      }}
    >
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar
        theme={isDark ? "dark" : "light"}
      />

      {/* ── Search + Add Bar ── */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 1,
          mb: 1,
          alignItems: { xs: "stretch", sm: "center" },
          position: "sticky",
          top: 0,
          zIndex: 1100,
          pt: 1,
          pb: 1,
          px: 0.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: (t) => alpha(t.palette.background.paper, 0.95),
          backdropFilter: "blur(8px)",
          boxShadow: isDark
            ? "0 2px 10px rgba(0,0,0,0.45)"
            : "0 1px 4px rgba(0,0,0,0.07)",
        }}
      >
        <TextField
          label="Search by Name or Symbol"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{
            flex: 1,
            minWidth: { xs: "100%", sm: "auto" },
            "& .MuiInputBase-root": {
              height: 30,
              fontSize: "0.7rem",
              borderRadius: "0px",
            },
            "& .MuiInputLabel-root": { fontSize: "0.7rem" },
          }}
        />

        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon sx={{ fontSize: 16 }} />}
          onClick={() => {
            setOpen(true);
            setEditId(null);
            setUnitForms([{ name: "", symbol: "" }]);
            setTouched([{ name: false, symbol: false }]);
          }}
          sx={{
            height: 30,
            fontSize: "0.7rem",
            padding: "2px 12px",
            borderRadius: "0px",
            textTransform: "none",
          }}
        >
          Unit
        </Button>
      </Box>

      {isMobile ? (
        /* ── MOBILE CARDS ── */
        <Box sx={{ bgcolor: "background.paper" }}>
          {filteredUnits.length > 0 ? (
            filteredUnits.map((unit, index) => (
              <Box key={unit.id}>
                {/* Main Row */}
                <Box
                  onClick={() =>
                    setExpandedUnit(
                      expandedUnit === unit.id ? null : unit.id
                    )
                  }
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1.5,
                    cursor: "pointer",
                    bgcolor: rowBg(index),
                    borderLeft: "4px solid",
                    borderLeftColor: "primary.main",
                    transition: "background-color 0.15s ease",
                    "&:active": { bgcolor: "action.selected" },
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 0.5,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.7rem",
                          color: "text.disabled",
                          minWidth: 25,
                        }}
                      >
                        #{index + 1}
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: "bold",
                          fontSize: "0.85rem",
                          flex: 1,
                          color: "text.primary",
                        }}
                      >
                        {unit.name}
                      </Typography>
                    </Box>

                    <Typography
                      sx={{ fontSize: "0.75rem", color: "text.secondary" }}
                    >
                      Symbol: {unit.symbol}
                    </Typography>
                  </Box>

                  <IconButton size="small" sx={{ ml: 1 }}>
                    {expandedUnit === unit.id ? (
                      <ExpandLess fontSize="small" />
                    ) : (
                      <ExpandMore fontSize="small" />
                    )}
                  </IconButton>
                </Box>

                {/* Expanded Details */}
                <Collapse
                  in={expandedUnit === unit.id}
                  timeout="auto"
                  unmountOnExit
                >
                  <Box
                    sx={{
                      px: 1.5,
                      py: 1,
                      bgcolor: (t) =>
                        t.palette.mode === "dark"
                          ? alpha(t.palette.common.white, 0.05)
                          : alpha(t.palette.primary.main, 0.04),
                      borderLeft: "4px solid",
                      borderLeftColor: "primary.main",
                    }}
                  >
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 0.8,
                        mb: 1,
                      }}
                    >
                      {/* S.NO */}
                      <Box>
                        <Typography
                          sx={{
                            fontSize: "0.6rem",
                            color: "text.disabled",
                            textTransform: "uppercase",
                            letterSpacing: "0.08em",
                            fontWeight: 600,
                          }}
                        >
                          S.NO
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.72rem",
                            fontWeight: 500,
                            color: "text.secondary",
                          }}
                        >
                          #{index + 1}
                        </Typography>
                      </Box>

                      {/* SYMBOL */}
                      <Box>
                        <Typography
                          sx={{
                            fontSize: "0.6rem",
                            color: "text.disabled",
                            textTransform: "uppercase",
                            letterSpacing: "0.08em",
                            fontWeight: 600,
                          }}
                        >
                          SYMBOL
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.72rem",
                            fontWeight: 500,
                            color: "text.primary",
                          }}
                        >
                          {unit.symbol}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Unit Name */}
                    <Box sx={{ mb: 0.8 }}>
                      <Typography
                        sx={{
                          fontSize: "0.6rem",
                          color: "text.disabled",
                          textTransform: "uppercase",
                          letterSpacing: "0.08em",
                          fontWeight: 600,
                          mb: 0.3,
                        }}
                      >
                        UNIT NAME
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.7rem",
                          color: "text.secondary",
                          lineHeight: 1.3,
                          fontWeight: 500,
                        }}
                      >
                        {unit.name}
                      </Typography>
                    </Box>

                    <Divider sx={{ my: 1, borderColor: "divider" }} />

                    {/* Action Buttons */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.6rem",
                          color: "text.disabled",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: "0.08em",
                        }}
                      >
                        ACTIONS
                      </Typography>

                      <Box sx={{ display: "flex", gap: 0.6 }}>
                        {/* Edit */}
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(unit);
                          }}
                          sx={{
                            bgcolor: (t) =>
                              t.palette.mode === "dark"
                                ? alpha(t.palette.primary.main, 0.15)
                                : "#e3f2fd",
                            borderRadius: "6px",
                            width: 32,
                            height: 32,
                            transition: "all 0.2s",
                            "&:hover": {
                              bgcolor: (t) =>
                                t.palette.mode === "dark"
                                  ? alpha(t.palette.primary.main, 0.28)
                                  : "#bbdefb",
                              transform: "scale(1.05)",
                            },
                          }}
                        >
                          <EditIcon
                            sx={{ fontSize: "1rem", color: "primary.main" }}
                          />
                        </IconButton>

                        {/* Delete */}
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClick(e, unit.id);
                          }}
                          sx={{
                            bgcolor: (t) =>
                              t.palette.mode === "dark"
                                ? alpha(t.palette.error.main, 0.15)
                                : "#ffebee",
                            borderRadius: "6px",
                            width: 32,
                            height: 32,
                            transition: "all 0.2s",
                            "&:hover": {
                              bgcolor: (t) =>
                                t.palette.mode === "dark"
                                  ? alpha(t.palette.error.main, 0.28)
                                  : "#ffcdd2",
                              transform: "scale(1.05)",
                            },
                          }}
                        >
                          <DeleteIcon
                            sx={{ fontSize: "1rem", color: "error.main" }}
                          />
                        </IconButton>
                      </Box>
                    </Box>
                  </Box>
                </Collapse>

                <Divider />
              </Box>
            ))
          ) : (
            <Box
              sx={{
                textAlign: "center",
                py: 4,
                color: "text.disabled",
              }}
            >
              <Typography>No matching records found</Typography>
            </Box>
          )}
        </Box>
      ) : (
        /* ── DESKTOP TABLE ── */
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            maxHeight: "88vh",
            overflowX: "auto",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            bgcolor: "background.paper",
            backgroundImage: "none",
            boxShadow: isDark ? "0 4px 18px rgba(0,0,0,0.35)" : "none",
          }}
        >
          <Table
            size="small"
            stickyHeader
            sx={{
              minWidth: 1000,
              "& td, & th": {
                textAlign: "center",
                whiteSpace: "nowrap",
                borderBottom: "1px solid",
                borderColor: "divider",
              },
            }}
          >
            <TableHead
              sx={{
                "& th": {
                  fontWeight: "bold",
                  color: "text.primary",
                  borderBottom: "2px solid",
                  borderColor: "primary.main",
                  backgroundColor: tableHeadBg,
                  fontSize: "0.75rem",
                  letterSpacing: "0.04em",
                },
              }}
            >
              <TableRow>
                <TableCell sx={{ backgroundColor: tableHeadBg }}>
                  S.No
                </TableCell>
                <TableCell sx={{ backgroundColor: tableHeadBg }}>
                  Name
                </TableCell>
                <TableCell sx={{ backgroundColor: tableHeadBg }}>
                  Symbol
                </TableCell>
                <TableCell sx={{ backgroundColor: tableHeadBg }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredUnits.length > 0 ? (
                filteredUnits.map((unit, index) => (
                  <TableRow
                    hover
                    key={unit.id}
                    sx={{
                      backgroundColor: rowBg(index),
                      transition: "background-color 0.15s ease",
                      "&:hover": {
                        backgroundColor: (t) =>
                          t.palette.mode === "dark"
                            ? `${alpha(
                                t.palette.primary.main,
                                0.1
                              )} !important`
                            : "#e3f2fd !important",
                      },
                      "& td": {
                        paddingRight: "13px !important",
                        paddingLeft: "13px !important",
                        padding: "0px",
                        fontSize: { xs: "0.65rem", sm: "0.75rem" },
                        whiteSpace: "nowrap",
                        color: "text.primary",
                      },
                    }}
                  >
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{unit.name}</TableCell>
                    <TableCell>{unit.symbol}</TableCell>
                    <TableCell>
                      <IconButton
                        sx={{
                          padding: 0,
                          paddingLeft: 1,
                          paddingRight: 1,
                        }}
                        size="small"
                        color="primary"
                        onClick={() => handleEdit(unit)}
                      >
                        <EditIcon transform="scale(0.6)" />
                      </IconButton>
                      <IconButton
                        sx={{
                          padding: 0,
                          paddingLeft: 1,
                          paddingRight: 1,
                        }}
                        size="small"
                        color="error"
                        onClick={(e) => handleDeleteClick(e, unit.id)}
                      >
                        <DeleteIcon transform="scale(0.6)" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    align="center"
                    sx={{ py: 3, color: "text.secondary" }}
                  >
                    No matching records found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ══════════════════════════════════════════════ */}
      {/* ADD / EDIT DIALOG                             */}
      {/* ══════════════════════════════════════════════ */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            bgcolor: "background.paper",
            backgroundImage: "none",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: isDark
              ? "0 12px 40px rgba(0,0,0,0.65)"
              : "0 8px 32px rgba(0,0,0,0.14)",
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
            py: 1.5,
            bgcolor: (t) =>
              t.palette.mode === "dark"
                ? alpha(t.palette.primary.main, 0.15)
                : alpha(t.palette.primary.main, 0.06),
            color: "text.primary",
            fontWeight: 700,
            fontSize: "0.95rem",
          }}
        >
          {editId ? "Edit Unit" : "Add Units"}
        </DialogTitle>

        <DialogContent dividers sx={{ bgcolor: "background.paper" }}>
          {editId ? (
            /* Edit Mode: Single Row */
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <TextField
                label="Unit Name"
                fullWidth
                value={unitForm.name}
                onChange={(e) =>
                  setUnitForm({ ...unitForm, name: e.target.value })
                }
              />
              <TextField
                label="Symbol"
                fullWidth
                value={unitForm.symbol}
                onChange={(e) =>
                  setUnitForm({ ...unitForm, symbol: e.target.value })
                }
              />
            </Box>
          ) : (
            /* Add Mode: Multiple Rows */
            <Box>
              {unitForms.map((form, index) => (
                <Box
                  key={index}
                  sx={{ display: "flex", gap: 2, mb: 2 }}
                >
                  <TextField
                    label="Unit Name"
                    fullWidth
                    value={form.name}
                    onChange={(e) =>
                      handleChange(index, "name", e.target.value)
                    }
                    onBlur={() => handleBlur(index, "name")}
                    error={touched[index]?.name && !form.name}
                    helperText={
                      touched[index]?.name && !form.name
                        ? "Unit Name is required"
                        : ""
                    }
                  />
                  <TextField
                    label="Symbol"
                    fullWidth
                    value={form.symbol}
                    onChange={(e) =>
                      handleChange(index, "symbol", e.target.value)
                    }
                    onBlur={() => handleBlur(index, "symbol")}
                    error={touched[index]?.symbol && !form.symbol}
                    helperText={
                      touched[index]?.symbol && !form.symbol
                        ? "Symbol is required"
                        : ""
                    }
                  />
                  {unitForms.length > 1 && (
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => handleRemoveField(index)}
                      sx={{
                        "&:hover": {
                          bgcolor: (t) =>
                            t.palette.mode === "dark"
                              ? alpha(t.palette.error.main, 0.15)
                              : "#ffebee",
                        },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>

        {/* ADD ROW row */}
        {!editId && (
          <DialogActions
            sx={{
              justifyContent: "flex-start",
              mb: -5,
              bgcolor: "background.paper",
            }}
          >
            <Button
              type="button"
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              onClick={handleAddRow}
              sx={{
                height: 35,
                fontSize: 14,
                padding: "2px 10px",
                minWidth: "auto",
                textTransform: "none",
                border: "none",
                display: "flex",
                alignSelf: "flex-start",
                bgcolor: "transparent",
                color: "primary.main",
                "&:hover": {
                  bgcolor: (t) =>
                    t.palette.mode === "dark"
                      ? alpha(t.palette.primary.main, 0.1)
                      : alpha(t.palette.primary.main, 0.06),
                },
              }}
            >
              ADD ROW
            </Button>
          </DialogActions>
        )}

        {/* Footer Actions */}
        <DialogActions
          sx={{
            borderTop: "1px solid",
            borderColor: "divider",
            px: 2,
            py: 1.2,
            bgcolor: "background.paper",
          }}
        >
          <Button
            color="error"
            variant="outlined"
            size="small"
            onClick={() => setOpen(false)}
          >
            CLOSE
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            size="small"
          >
            {editId ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Popover ── */}
      <Popover
        open={openPopover}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        PaperProps={{
          sx: {
            bgcolor: "background.paper",
            backgroundImage: "none",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: isDark
              ? "0 8px 28px rgba(0,0,0,0.6)"
              : "0 4px 16px rgba(0,0,0,0.14)",
          },
        }}
      >
        <Box sx={{ p: 2, minWidth: 180 }}>
          <Typography
            variant="body2"
            sx={{ color: "text.primary", mb: 1 }}
          >
            Are you sure you want to delete?
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              mt: 1,
            }}
          >
            <Button
              size="small"
              variant="outlined"
              onClick={handlePopoverClose}
            >
              No
            </Button>
            <Button
              color="error"
              variant="contained"
              size="small"
              onClick={confirmDelete}
            >
              Yes
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export default UnitManager;